import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash-es';
import { Search } from '../../icons';
import { useBiAction } from '../BiTracking';
import { CloseButton } from '../CloseButton';
import { useOverlayContext } from '../OverlayContext';
import styles from './Searchbox.module.scss';
export function Searchbox(props) {
    const { value, className, placeholder, debounceTime, registerHotkey = false, showHint = true, variant = 'chip', fluidWidth, autoFocus, onChange, onClear, onInputBlur, onInputFocus, testId, bi, noBi: _noBi } = props, rest = _object_without_properties(props, [
        "value",
        "className",
        "placeholder",
        "debounceTime",
        "registerHotkey",
        "showHint",
        "variant",
        "fluidWidth",
        "autoFocus",
        "onChange",
        "onClear",
        "onInputBlur",
        "onInputFocus",
        "testId",
        "bi",
        "noBi"
    ]);
    const logBiAction = useBiAction();
    const [inputValue, setInputValue] = useState(value || '');
    const { hasOpenModals } = useOverlayContext();
    const inputValueBeforeDebounce = useRef(inputValue);
    const debouncedOnChange = useMemo(()=>{
        return debounce((event)=>{
            if (inputValueBeforeDebounce.current !== event.target.value) {
                onChangeRef.current(event);
            }
        }, debounceTime);
    }, [
        debounceTime
    ]);
    const wrappedOnChange = (event)=>{
        inputValueBeforeDebounce.current = event.target.value;
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'searchbox',
                entityName: bi.name,
                actionType: 'text_input',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        onChange && onChange(event);
    };
    const onChangeRef = useRef(wrappedOnChange);
    onChangeRef.current = wrappedOnChange;
    const debouncedOnChangeRef = useRef(debouncedOnChange);
    debouncedOnChangeRef.current = debouncedOnChange;
    const searchBoxRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isHotkeyRegistered, setIsHotkeyRegistered] = useState(false);
    useEffect(()=>{
        function onKeyDown(ev) {
            if (ev.key === '/') {
                ev.preventDefault();
                searchBoxRef.current != null && searchBoxRef.current.focus();
            }
        }
        if (registerHotkey && !isFocused && !hasOpenModals) {
            document.addEventListener('keydown', onKeyDown, true);
            setIsHotkeyRegistered(true);
            return ()=>{
                document.removeEventListener('keydown', onKeyDown, true);
                setIsHotkeyRegistered(false);
            };
        }
    }, [
        registerHotkey,
        isFocused,
        hasOpenModals
    ]);
    useEffect(()=>{
        setInputValue(value || '');
    }, [
        value
    ]);
    useEffect(()=>{
        return ()=>{
            debouncedOnChangeRef.current.cancel();
        };
    }, []);
    function renderSearchIcon() {
        switch(variant){
            case 'chip':
                return /*#__PURE__*/ _jsx(Search, {
                    size: 20,
                    className: styles.smallSearchIcon
                });
            case 'textfield':
                return /*#__PURE__*/ _jsx(Search, {
                    className: styles.searchIcon
                });
        }
    }
    return /*#__PURE__*/ _jsxs("div", _object_spread_props(_object_spread({
        className: cn(styles.root, {
            [styles.textfield]: variant === 'textfield'
        }, {
            [styles.fluidWidth]: fluidWidth
        }, className),
        "data-testid": testId
    }, rest), {
        children: [
            renderSearchIcon(),
            /*#__PURE__*/ _jsx("input", {
                ref: searchBoxRef,
                type: "text",
                autoFocus: autoFocus,
                className: cn(styles.input, inputValue.length > 0 ? styles.notEmpty : null),
                value: inputValue,
                placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : 'Search...',
                "data-testid": testId && "".concat(testId, "-input"),
                "data-hotkey-registered": isHotkeyRegistered,
                onChange: (event)=>{
                    setInputValue(event.target.value);
                    if (debounceTime) {
                        event.persist();
                        debouncedOnChange(event);
                    } else {
                        onChangeRef.current(event);
                    }
                },
                onFocus: (ev)=>{
                    setIsFocused(true);
                    onInputFocus === null || onInputFocus === void 0 ? void 0 : onInputFocus(ev);
                },
                onBlur: (ev)=>{
                    setIsFocused(false);
                    onInputBlur === null || onInputBlur === void 0 ? void 0 : onInputBlur(ev);
                }
            }),
            registerHotkey && showHint && /*#__PURE__*/ _jsx("div", {
                className: styles.hotkeyHint,
                children: /*#__PURE__*/ _jsx("kbd", {
                    children: "/"
                })
            }),
            !!inputValue && /*#__PURE__*/ _jsx(CloseButton, {
                noBi: true,
                className: styles.clear,
                size: "small",
                testId: testId && "".concat(testId, "-clear"),
                onClick: ()=>{
                    // In uncontrolled mode, clear the value automatically.
                    if (typeof value !== 'string') {
                        setInputValue('');
                    }
                    onClear && onClear();
                }
            })
        ]
    }));
}
